'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from './cnNew'
import { CheckIcon, ChevronDownIcon, SearchIcon } from './iconsNew'

/**
 * Select accesible (listbox) construido a mano.
 *
 * options: [{ value, label, group? }]
 * Soporta grupos, búsqueda opcional y navegación con teclado.
 */
export function Select({
  value,
  onChange,
  options = [],
  placeholder = 'Selecciona…',
  size = 'md',
  searchable = false,
  disabled = false,
  className,
  contentWidth,
  ariaLabel,
  triggerClassName,
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const wrapperRef = useRef(null)
  const listRef = useRef(null)
  const searchRef = useRef(null)

  const filtered = useMemo(() => {
    if (!query.trim()) return options
    const term = query.trim().toLowerCase()
    return options.filter((option) => String(option.label).toLowerCase().includes(term))
  }, [options, query])

  const grouped = useMemo(() => {
    const groups = []
    filtered.forEach((option) => {
      const name = option.group || ''
      let group = groups.find((item) => item.name === name)
      if (!group) {
        group = { name, options: [] }
        groups.push(group)
      }
      group.options.push(option)
    })
    return groups
  }, [filtered])

  const selected = options.find((option) => String(option.value) === String(value))

  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  useEffect(() => {
    if (open) {
      setQuery('')
      const index = filtered.findIndex((option) => String(option.value) === String(value))
      setHighlight(index >= 0 ? index : 0)
      if (searchable) requestAnimationFrame(() => searchRef.current?.focus())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const commit = (option) => {
    onChange(option.value)
    setOpen(false)
  }

  const onKeyDown = (event) => {
    if (event.nativeEvent.isComposing || event.keyCode === 229) return

    if (!open && ['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault()
      setOpen(true)
      return
    }
    if (!open) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlight((prev) => Math.min(prev + 1, filtered.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlight((prev) => Math.max(prev - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const option = filtered[highlight]
      if (option) commit(option)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
    } else if (event.key === 'Tab') {
      setOpen(false)
    }
  }

  const heights = { sm: 'h-8 text-[13px] px-2.5', md: 'h-10 text-sm px-3' }

  let flatIndex = -1

  return (
    <div ref={wrapperRef} className={cn('relative w-full', className)}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={onKeyDown}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg border bg-card text-left font-medium transition-colors',
          'border-input hover:border-ring/60 disabled:opacity-50 disabled:hover:border-input',
          open && 'border-ring',
          heights[size],
          triggerClassName
        )}
      >
        <span className={cn('truncate', !selected && 'font-normal text-muted-foreground')}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDownIcon
          size={16}
          className={cn('shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div
          style={contentWidth ? { width: contentWidth } : undefined}
          className="absolute z-50 mt-1 w-full min-w-full rounded-xl border border-border bg-popover p-1 shadow-[0_12px_32px_rgba(16,42,48,0.14)]"
        >
          {searchable && (
            <div className="mb-1 flex items-center gap-2 rounded-lg bg-muted px-2.5">
              <SearchIcon size={14} className="shrink-0 text-muted-foreground" />
              <input
                ref={searchRef}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value)
                  setHighlight(0)
                }}
                onKeyDown={onKeyDown}
                placeholder="Buscar…"
                className="h-8 w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
                aria-label="Buscar opción"
              />
            </div>
          )}

          <ul
            ref={listRef}
            role="listbox"
            className="scrollbar-slim max-h-64 overflow-y-auto"
            tabIndex={-1}
          >
            {filtered.length === 0 && (
              <li className="px-2.5 py-3 text-center text-[13px] text-muted-foreground">
                Sin resultados
              </li>
            )}

            {grouped.map((group) => (
              <li key={group.name || 'default'}>
                {group.name && (
                  <p className="px-2.5 pt-2 pb-1 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
                    {group.name}
                  </p>
                )}
                <ul role="group">
                  {group.options.map((option) => {
                    flatIndex += 1
                    const index = flatIndex
                    const isSelected = String(option.value) === String(value)
                    return (
                      <li
                        key={`${option.value}`}
                        role="option"
                        aria-selected={isSelected}
                        onMouseEnter={() => setHighlight(index)}
                        onClick={() => commit(option)}
                        className={cn(
                          'flex cursor-pointer items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-[13px]',
                          highlight === index ? 'bg-accent text-accent-foreground' : 'text-foreground',
                          isSelected && 'font-semibold'
                        )}
                      >
                        <span className="truncate">{option.label}</span>
                        {isSelected && <CheckIcon size={14} className="shrink-0 text-primary" />}
                      </li>
                    )
                  })}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
